import React, { useContext, useState, useCallback } from 'react';
import Swal from 'sweetalert2';
import moment from 'moment';
import ReactDatetime from 'react-datetime';
import {
  Container,
  Row,
  Col,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Button,
  Card,
  CardBody,
  Badge,
} from 'reactstrap';

import CustomTable from '../components/CustomTable.jsx';
import PageHeader from '../../../components/Headers/PageHeader.jsx';
import { AdminStateContext } from '../../../providers/AdminStateProvider.jsx';
import { getTreasuryMovements, patchTreasuryAgreedDate } from '../../../api';
import { useAuthToken } from '../../../hooks/AuthHook.jsx';
import { swalErrorMessage } from '../utils';
import {
  columns,
  sizes,
  PER_PAGE,
  typeOptions,
  conceptOptions,
  statusOptions,
  formatDate,
  formatAmount,
  parseAndNormalizeDateInput,
} from './constants';

/** Solo pasar a ReactDatetime un string YYYY-MM-DD válido o vacío (evita pantalla en blanco). */
const toValidDateString = (ymd) => {
  if (!ymd || typeof ymd !== 'string' || ymd.length !== 10) return '';
  const m = moment(ymd, 'YYYY-MM-DD');
  return m.isValid() ? ymd : '';
};

const BulkUpdateAgreedDate = () => {
  const [adminState] = useContext(AdminStateContext);
  const authToken = useAuthToken();
  const country = adminState?.country ?? '';

  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedConcept, setSelectedConcept] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [subscriptionIds, setSubscriptionIds] = useState('');
  const [propertyIds, setPropertyIds] = useState('');

  // Preview state
  const [movements, setMovements] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isFetching, setIsFetching] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  // Bulk update state
  const [newAgreedDate, setNewAgreedDate] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  const totalPages = Math.ceil(total / PER_PAGE);

  const buildFilters = useCallback(() => {
    const filters = {
      from: dateFrom,
      to: dateTo,
      per_page: PER_PAGE,
    };
    if (selectedType !== 'all') filters.movement_type = [selectedType];
    if (selectedConcept !== 'all') filters.movement_concept = [selectedConcept];
    if (selectedStatus !== 'all') filters.movement_status = [selectedStatus];
    if (subscriptionIds.trim()) {
      filters.subscription_ids = subscriptionIds.split(',').map((s) => s.trim()).filter(Boolean);
    }
    if (propertyIds.trim()) {
      filters.property_ids = propertyIds.split(',').map((s) => s.trim()).filter(Boolean);
    }
    return filters;
  }, [dateFrom, dateTo, selectedType, selectedConcept, selectedStatus, subscriptionIds, propertyIds]);

  const fetchMovements = useCallback(async (targetPage = 1) => {
    if (!dateFrom || !dateTo) return;
    setIsFetching(true);
    try {
      const filters = buildFilters();
      filters.page = targetPage;
      const response = await getTreasuryMovements(
        country.toLowerCase(),
        authToken,
        filters,
      );
      setTotal(response.total || 0);
      setPage(response.page || targetPage);
      const parsed = (response.movements || []).map((m) => ({
        id: m.id,
        data: [
          m.id ?? '-',
          m.type || '-',
          m.concept || '-',
          m.status || '-',
          formatDate(m.agreed_payment_date),
          formatAmount(m.total_amount),
          m.property_id || '-',
          m.subscription_id || '-',
        ],
      }));
      setMovements(parsed);
      setHasFetched(true);
    } catch (e) {
      swalErrorMessage('Error al consultar movimientos. Intente nuevamente.');
    } finally {
      setIsFetching(false);
    }
  }, [authToken, country, buildFilters, dateFrom, dateTo]);

  const handlePageChange = useCallback((newPage) => {
    fetchMovements(newPage);
  }, [fetchMovements]);

  const handlePreview = () => {
    setPage(1);
    fetchMovements(1);
  };

  const buildPatchBody = useCallback(() => {
    const body = {
      from: dateFrom,
      to: dateTo,
      new_agreed_date: newAgreedDate,
      expected_count: total,
    };
    if (selectedType !== 'all') body.movement_type = [selectedType];
    if (selectedConcept !== 'all') body.movement_concept = [selectedConcept];
    if (selectedStatus !== 'all') body.movement_status = [selectedStatus];
    if (subscriptionIds.trim()) {
      body.subscription_ids = subscriptionIds.split(',').map((s) => Number(s.trim())).filter(Boolean);
    }
    if (propertyIds.trim()) {
      body.property_ids = propertyIds.split(',').map((s) => Number(s.trim())).filter(Boolean);
    }
    return body;
  }, [dateFrom, dateTo, newAgreedDate, selectedType, selectedConcept, selectedStatus, subscriptionIds, propertyIds, total]);

  const handleBulkUpdate = async () => {
    if (!newAgreedDate) {
      swalErrorMessage('Debe seleccionar una nueva fecha acordada.');
      return;
    }

    const result = await Swal.fire({
      title: 'Confirmar cambio masivo',
      html: `Va a actualizar la fecha acordada de <strong>${total}</strong> movimiento(s) a <strong>${formatDate(newAgreedDate)}</strong>.<br/><br/>Esta accion no se puede deshacer.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#5e72e4',
      cancelButtonColor: '#f5365c',
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
    });

    if (!result.isConfirmed) return;

    setIsUpdating(true);
    try {
      const body = buildPatchBody();
      const response = await patchTreasuryAgreedDate(
        country.toLowerCase(),
        authToken,
        body,
      );
      await Swal.fire({
        title: 'Actualizado',
        text: `Se actualizaron ${response.matched_count} movimiento(s) a la fecha ${formatDate(response.new_agreed_date)}.`,
        icon: 'success',
      });
      // Refresh preview
      fetchMovements(1);
    } catch (err) {
      let message = 'Error al actualizar. Intente nuevamente.';
      if (err?.error === 'COUNT_MISMATCH') {
        message = `Los datos cambiaron desde la última vista previa (esperados: ${err.expected}, actuales: ${err.actual}). Vuelva a consultar antes de actualizar.`;
      } else if (err?.error === 'BULK_UPDATE_LIMIT_EXCEEDED') {
        message = `La operación afectaría ${err.actual} movimientos, excediendo el límite de ${err.limit}. Use filtros más específicos.`;
      } else if (err?.message) {
        message = err.message;
      }
      swalErrorMessage(message);
    } finally {
      setIsUpdating(false);
    }
  };

  const canPreview = dateFrom && dateTo;
  const canUpdate = hasFetched && total > 0 && newAgreedDate && !isUpdating;

  return (
    <div>
      <PageHeader
        title="Cambio masivo de fechas"
        breadcrumb={[
          { href: '#', title: 'Herramientas' },
          { href: '#', title: 'Cambio masivo de fechas' },
        ]}
      />
      <Container fluid>
        {/* Filters */}
        <Card className="mb-4">
          <CardBody>
            <h4 className="mb-3">Filtros</h4>
            <Form>
              <Row>
                <Col md="3">
                  <FormGroup>
                    <label className="form-control-label">Desde *</label>
                    <InputGroup className="input-group-alternative">
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText>
                          <i className="ni ni-calendar-grid-58" />
                        </InputGroupText>
                      </InputGroupAddon>
                      <ReactDatetime
                        inputProps={{
                          placeholder: 'DD/MM/YYYY',
                          onBlur: (e) => {
                            const ymd = parseAndNormalizeDateInput(e.target.value);
                            if (ymd) setDateFrom(ymd);
                          },
                        }}
                        value={toValidDateString(dateFrom)}
                        timeFormat={false}
                        dateFormat="DD/MM/YYYY"
                        closeOnSelect
                        onChange={(e) => {
                          if (moment.isMoment(e) && e.isValid()) setDateFrom(e.format('YYYY-MM-DD'));
                          else if (e === '') setDateFrom('');
                        }}
                      />
                    </InputGroup>
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup>
                    <label className="form-control-label">Hasta *</label>
                    <InputGroup className="input-group-alternative">
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText>
                          <i className="ni ni-calendar-grid-58" />
                        </InputGroupText>
                      </InputGroupAddon>
                      <ReactDatetime
                        inputProps={{
                          placeholder: 'DD/MM/YYYY',
                          onBlur: (e) => {
                            const ymd = parseAndNormalizeDateInput(e.target.value);
                            if (ymd) setDateTo(ymd);
                          },
                        }}
                        value={toValidDateString(dateTo)}
                        timeFormat={false}
                        dateFormat="DD/MM/YYYY"
                        closeOnSelect
                        onChange={(e) => {
                          if (moment.isMoment(e) && e.isValid()) setDateTo(e.format('YYYY-MM-DD'));
                          else if (e === '') setDateTo('');
                        }}
                      />
                    </InputGroup>
                  </FormGroup>
                </Col>
                <Col md="2">
                  <FormGroup>
                    <label className="form-control-label">Tipo</label>
                    <Input
                      type="select"
                      value={selectedType}
                      onChange={(e) => setSelectedType(e.target.value)}
                    >
                      <option value="all">-- Todos --</option>
                      {typeOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="2">
                  <FormGroup>
                    <label className="form-control-label">Concepto</label>
                    <Input
                      type="select"
                      value={selectedConcept}
                      onChange={(e) => setSelectedConcept(e.target.value)}
                    >
                      <option value="all">-- Todos --</option>
                      {conceptOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="2">
                  <FormGroup>
                    <label className="form-control-label">Estado</label>
                    <Input
                      type="select"
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                    >
                      <option value="all">-- Todos --</option>
                      {statusOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="3">
                  <FormGroup>
                    <label className="form-control-label">IDs de suscripcion</label>
                    <Input
                      type="text"
                      placeholder="Ej: 100, 200, 300"
                      value={subscriptionIds}
                      onChange={(e) => setSubscriptionIds(e.target.value)}
                    />
                    <small className="text-muted">Separados por coma</small>
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup>
                    <label className="form-control-label">IDs de propiedad</label>
                    <Input
                      type="text"
                      placeholder="Ej: 10, 20, 30"
                      value={propertyIds}
                      onChange={(e) => setPropertyIds(e.target.value)}
                    />
                    <small className="text-muted">Separados por coma</small>
                  </FormGroup>
                </Col>
                <Col md="3" className="d-flex align-items-end">
                  <FormGroup>
                    <Button
                      color="primary"
                      onClick={handlePreview}
                      disabled={!canPreview || isFetching}
                    >
                      {isFetching ? 'Consultando...' : 'Previsualizar'}
                    </Button>
                  </FormGroup>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>

        {/* Results */}
        {hasFetched && (
          <Row className="mb-3">
            <Col>
              <Badge color="info" className="mr-2 p-2">
                Total movimientos: {total}
              </Badge>
              <Badge color="secondary" className="p-2">
                Pagina {page} de {totalPages || 1}
              </Badge>
            </Col>
          </Row>
        )}

        <CustomTable
          columns={columns}
          sizes={sizes}
          data={movements}
          loading={isFetching}
          page={page}
          totalPages={totalPages}
          changePage={handlePageChange}
          renderHeader={() => null}
        />

        {/* Bulk update action */}
        {hasFetched && total > 0 && (
          <Card className="mt-4">
            <CardBody>
              <h4 className="mb-3">Actualizar fecha acordada</h4>
              <Row className="align-items-end">
                <Col md="3">
                  <FormGroup>
                    <label className="form-control-label">Nueva fecha acordada *</label>
                    <InputGroup className="input-group-alternative">
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText>
                          <i className="ni ni-calendar-grid-58" />
                        </InputGroupText>
                      </InputGroupAddon>
                      <ReactDatetime
                        inputProps={{
                          placeholder: 'DD/MM/YYYY',
                          onBlur: (e) => {
                            const ymd = parseAndNormalizeDateInput(e.target.value);
                            if (ymd) setNewAgreedDate(ymd);
                          },
                        }}
                        value={toValidDateString(newAgreedDate)}
                        timeFormat={false}
                        dateFormat="DD/MM/YYYY"
                        closeOnSelect
                        onChange={(e) => {
                          if (moment.isMoment(e) && e.isValid()) setNewAgreedDate(e.format('YYYY-MM-DD'));
                          else if (e === '') setNewAgreedDate('');
                        }}
                      />
                    </InputGroup>
                  </FormGroup>
                </Col>
                <Col md="4" className="d-flex align-items-end">
                  <FormGroup>
                    <Button
                      color="danger"
                      onClick={handleBulkUpdate}
                      disabled={!canUpdate}
                    >
                      {isUpdating ? 'Actualizando...' : `Actualizar fecha de ${total} movimiento(s)`}
                    </Button>
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>
        )}
      </Container>
    </div>
  );
};

export default BulkUpdateAgreedDate;
